import { Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Modal from 'react-responsive-modal';
import { toast } from 'react-toastify';
import * as pdfjsLib from 'pdfjs-dist';
import { Page } from '../../components';
import PDFViewer from '../../components/PDFViewer';
import { ReactComponent as StepComplete } from '../../assets/images/step-complete.svg';
import { ReactComponent as RadioChecked } from '../../assets/images/radio-button-checked.svg';
import { ReactComponent as RadioUnchecked } from '../../assets/images/radio-button-unchecked.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/info-stroke.svg';
import axios from '../../_actions/axiosInstance';
import config from '../../_config/config';
import Notification from '../../components/Notification';
import useMappingToolStore, { extractErrorMessageAsync } from './store';

const notifyFailure = message => toast.info(<Notification title="Fail" text={message} />);

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

const STEPS = [
  { key: 'upload', label: 'Upload file' },
  { key: 'mapping', label: 'Mapping' },
  { key: 'review', label: 'Review' },
  { key: 'testing', label: 'Testing' }
];

const CURRENT_STEP_INDEX = 3;
const REVIEW_PATH = '/report_template_mapper/review';
const LIST_PATH = '/report_template_mapper/list';

const TestingMappedPDF = () => {
  const history = useHistory();
  const [
    { file, formName, formState, formYear, stateFormId },
    { buildMappingPayload, clearMapping }
  ] = useMappingToolStore();

  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const goToReview = () => history.push(REVIEW_PATH);
  const goToList = () => history.push(LIST_PATH);

  const closePdfPreview = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl('');
    setStatus('');
  };

  useEffect(
    () => () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl]
  );

  const loadPdfDoc = async () => {
    const buffer = await file.arrayBuffer();
    return pdfjsLib.getDocument({ data: buffer }).promise;
  };

  const runTest = async () => {
    if (!stateFormId) {
      setError("This form hasn't finished uploading yet — go back to Upload file.");
      return;
    }
    if (!file) {
      setError('No PDF loaded — go back to Mapping to select one.');
      return;
    }

    setIsRunning(true);
    setError('');
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
    }

    try {
      setStatus('Fulfilling this mapping against a real example inspection…');
      const pdfDoc = await loadPdfDoc();
      const mappingPayload = await buildMappingPayload(pdfDoc);

      const url = config.api.api_url + config.api.mapping_tool.testFulfillment;
      const res = await axios.post(url, mappingPayload, { responseType: 'blob' });

      const blob = new Blob([res.data], { type: 'application/pdf' });
      setPdfUrl(URL.createObjectURL(blob));
      setStatus('Showing this mapping fulfilled against a real example inspection.');
    } catch (err) {
      const message = await extractErrorMessageAsync(err, 'Could not fulfill the preview.');
      setError(message);
      notifyFailure(message);
      setStatus('');
    } finally {
      setIsRunning(false);
    }
  };

  // Runs on mount -- no manual trigger.
  useEffect(() => {
    runTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmSaveMapping = async () => {
    setIsSaving(true);
    try {
      const pdfDoc = await loadPdfDoc();
      const payload = await buildMappingPayload(pdfDoc);
      await axios.post(
        config.api.api_url + config.api.mapping_tool.saveMappedStateFormPdf,
        payload
      );
      setIsSaveModalOpen(false);
      // Clear the run so the next one starts fresh. The PDF is kept -- the saved mapping owns it.
      clearMapping();
      goToList();
    } catch (err) {
      const message = await extractErrorMessageAsync(err, 'Could not save the mapping.');
      setError(message);
      notifyFailure(message);
      setIsSaveModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  const pdfTitle = [formState, formName, formYear].filter(Boolean).join(' | ') || 'Untitled form';

  return (
    <Page
      documentName="Inspection Report Template Mapper"
      pageName="Inspection Report Template Mapper - Testing"
      path="/report_template_mapper/testing"
    >
      <div className="mapping-tool-page">
        <div className="mapping-tool-header d-flex align-items-center justify-content-between">
          <div>
            <h1 className="mapping-tool-title">Inspection Report Template Mapper</h1>
            <p className="mapping-tool-subtitle">Test this mapping against a real inspection</p>
          </div>
          <div className="upload-form-header-actions">
            <button type="button" className="btn upload-form-cancel-btn" onClick={goToReview}>
              Return
            </button>
            <button
              type="button"
              className="btn btn-primary mapping-tool-new-btn"
              onClick={() => setIsSaveModalOpen(true)}
            >
              Save Mapped Inspection Form
            </button>
          </div>
        </div>

        <div className="mapping-tool-list-card">
          <div className="upload-form-stepper">
            {STEPS.map((step, index) => (
              <Fragment key={step.key}>
                <div className="upload-form-step">
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {index < CURRENT_STEP_INDEX ? (
                    <StepComplete className="upload-form-step-icon" />
                  ) : index === CURRENT_STEP_INDEX ? (
                    <RadioChecked className="upload-form-step-icon" />
                  ) : (
                    <RadioUnchecked className="upload-form-step-icon" />
                  )}
                  <span className="upload-form-step-label">{step.label}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <span
                    className={`upload-form-step-line ${
                      index < CURRENT_STEP_INDEX ? 'active' : ''
                    }`}
                  />
                )}
              </Fragment>
            ))}
          </div>
        </div>

        <div className="upload-form-info-banner">
          <InfoIcon className="upload-form-info-icon" />
          <p className="upload-form-info-text mapping-tool-info-single-line">
            {error ||
              status ||
              (isRunning
                ? 'Running…'
                : 'Fulfills this mapping against a real example inspection for this client and question set — nothing is saved until you click Save Mapped State Form.')}
          </p>
        </div>

        <div className="mapping-tool-pdf-card">
          <p className="mapping-tool-pdf-title">{pdfTitle}</p>
          {!pdfUrl && (
            <div className="mapping-tool-pdf-stage mapping-tool-pdf-empty">
              <p className="mapping-tool-pdf-placeholder">
                {isRunning ? 'Running the preview…' : 'No fulfilled PDF yet.'}
              </p>
            </div>
          )}
          {pdfUrl && (
            <div style={{ height: '80vh' }}>
              <PDFViewer src={pdfUrl} onClose={closePdfPreview} title={pdfTitle} />
            </div>
          )}
        </div>
      </div>

      <Modal
        open={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        center
        focusTrapped={false}
        showCloseIcon={false}
        classNames={{ modal: 'mapping-tool-save-modal' }}
      >
        <div className="mapping-tool-save-modal-header">
          <div>
            <h2 className="mapping-tool-save-modal-title">Save mapped inspection form</h2>
            <p className="mapping-tool-save-modal-subtitle">Confirm Mapping</p>
            <p className="mapping-tool-save-modal-body">
              Once saved, mapped files cannot be edited. Please review your selections carefully
              before proceeding.
            </p>
          </div>
          <button
            type="button"
            className="mapping-tool-save-modal-close"
            aria-label="Close"
            onClick={() => setIsSaveModalOpen(false)}
            disabled={isSaving}
          >
            ×
          </button>
        </div>
        <div className="mapping-tool-save-modal-buttons">
          <button
            type="button"
            className="mapping-tool-save-modal-return-btn"
            onClick={() => setIsSaveModalOpen(false)}
            disabled={isSaving}
          >
            Return
          </button>
          <button
            type="button"
            className="mapping-tool-save-modal-confirm-btn"
            onClick={confirmSaveMapping}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </Modal>
    </Page>
  );
};

export default TestingMappedPDF;
